// auth.service.ts

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment'; // Ajusta la ruta según tu estructura de carpetas
import { signal, WritableSignal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private apiUrl = `${environment.apiUrl}/auth`;
  private static readonly TOKEN_KEY = 'admin_token';
  private static readonly USER_KEY = 'admin_user';

  private user: any = null;
  public currentUser: WritableSignal<any> = signal<any>(null);
  private token: string | null = null;

  constructor(private http: HttpClient, private router: Router) {
    this.token = localStorage.getItem(AuthService.TOKEN_KEY);
    const cachedUser = localStorage.getItem(AuthService.USER_KEY);
    if (cachedUser) {
      try {
        this.user = JSON.parse(cachedUser);
        this.currentUser.set(this.user);
      } catch {
        this.user = null;
      }
    }
  }

  // 🔐 Login to backend
  async login(credentials: { email: string; password: string; rememberMe?: boolean }): Promise<void> {
    const response = await this.http
      .post<any>(`${this.apiUrl}/login`, credentials, { withCredentials: true })
      .toPromise();

    // CORRECT: gets the user from response.data.user
    this.user = response?.data?.user ?? response?.user ?? null;
    const token =
      response?.data?.token ??
      response?.token ??
      response?.data?.accessToken ??
      response?.accessToken ??
      response?.data?.jwt ??
      null;
    this.setToken(token);
    this.persistUser();
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem(AuthService.TOKEN_KEY);
    }
    return this.token;
  }

  // 👤 Get current user from memory or backend
  async getUser(): Promise<any> {
    if (this.user) return this.user;

    try {
      const response: any = await this.http
        .get(`${this.apiUrl}/me`, { withCredentials: true })
        .toPromise();
      this.user = response?.data?.user ?? response?.user ?? null;
      this.persistUser();
      return this.user;
    } catch (err) {
      this.user = null;
      return null; // 👈  Don't logout here! Just put null.
    }
  }

  // 🔒 Check if the user is authenticated
  isLoggedIn(): boolean {
    return !!this.user;
  }

  // 🔁 Get user role
  getRole(): string | null {
    return this.user?.role || null;
  }

  // 👤 Get current user from memory
  getCurrentUser(): any {
    return this.user;
  }

  // 📝 Update current user if it matches
  updateCurrentUser(userData: any): void {
    if (this.user && this.user.id_user === userData.id_user) {
      this.user = { ...this.user, ...userData };
      this.persistUser();
    }
  }

  // 🚪 Logout: backend & redirección
  logout(): void {
    this.http
      .post(`${this.apiUrl}/logout`, {}, { withCredentials: true })
      .subscribe({
        next: () => {
          this.clearSession();
          this.router.navigate(['/login']);
        },
        error: () => {
          this.clearSession();
          this.router.navigate(['/login']);
        },
      });
  }

  private setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem(AuthService.TOKEN_KEY, token);
    } else {
      localStorage.removeItem(AuthService.TOKEN_KEY);
    }
  }

  private persistUser(): void {
    if (this.user) {
      localStorage.setItem(AuthService.USER_KEY, JSON.stringify(this.user));
    } else {
      localStorage.removeItem(AuthService.USER_KEY);
    }
    this.currentUser.set(this.user);
  }

  private clearSession(): void {
    this.user = null;
    this.setToken(null);
    localStorage.removeItem(AuthService.USER_KEY);
    this.currentUser.set(null);
  }
}
